import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { Select } from 'primeng/select';
import { ApiService } from 'src/app/core/services/api.service';
import { ConfigurationService } from 'src/app/core/services/configuration.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { BelenderStatusCodes } from 'src/app/models/BelenderStatusCodes';
import { BelenderWebShocketMessage } from 'src/app/models/BelenderWebShocketMessage';
import { LabeledValue } from 'src/app/models/LabeledValue';
import { NewRequestStatus } from 'src/app/models/NewRequestStatus';
import { PhysicalPersonQueryResponse } from 'src/app/models/OpenData.PhysicalPersonQueryResponse';
import { AvailableQueries, DocumentationPack } from 'src/app/models/Security.User';
import { AdminMessagesComponent } from 'src/app/shared/components/admin-messages/admin-messages.component';
import { QrCodeComponent } from 'src/app/shared/components/qr-code/qr-code.component';
import { DNI, INVALID, SEPARATOR } from 'src/app/shared/constants/validation.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { environment } from 'src/environments/environment';
import { InsideDisableComponent } from '../../../utils/inside-disable/inside-disable.component';
import { LoadingComponent } from '../../../utils/loading/loading.component';
import { NewCardRequestService } from './new-card-request.service';

@Component({
    selector: 'app-new-card-request',
    templateUrl: './new-card-request.component.html',
    styleUrl: './new-card-request.component.scss',
    standalone: true,
    imports: [
        ToastModule,
        PrimeTemplate,
        NgFor,
        ConfirmDialogModule,
        NgClass,
        NgIf,
        RadioButtonModule,
        FormsModule,
        TooltipModule,
        LoadingComponent,
        InsideDisableComponent,
        ReactiveFormsModule,
        InputTextModule,
        DatePickerModule,
        CheckboxModule,
        SelectModule,
        DatePipe,
        TranslateModule,
        AdminMessagesComponent,
        QrCodeComponent
    ],
})
export class NewCardRequestComponent implements OnInit {

    physicalPersonQueryResponse: PhysicalPersonQueryResponse;

	@Input() newRequestStatus: NewRequestStatus = { title: 'New request', step: 1 };
	@Input() trustedConsentUrlBoxed: any;
    @ViewChild('adminMessages') adminMessagesComponent: AdminMessagesComponent;
	@ViewChild('documentationPackDropdown') documentationPackDropdown: Select;

	isLoading = false;
	formIsDisabled = false;

	documentationPackValues: DocumentationPack[] = [];
	private readonly waitingMessagesTranslationKeys = ['NEW_REQUEST.WAITING_MESSAGES.WAIT_1', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_2', 'NEW_REQUEST.WAITING_MESSAGES.WAIT_3'];
	waitingMessages = [];

	private invalidFields = [];
	private requiredFieldsMissing = false;

	selectedRequestorType = 'Physical_Person';
	selectedRequestType = 'Clave_Pin';

	availableQueries: AvailableQueries;

	@Input() belenderStatus: {
		actualStatus: string;
		actualStatusMessage: string;
	} = { actualStatus: '', actualStatusMessage: '' };

	isLoadingRequest = false;

	isAdminOk = true;
	isConsentChecked = false;

	belenderWidgetLangOptions: LabeledValue[] = [
	    { label: 'LANGUAGES.ES', value: 'es' },
	    { label: 'LANGUAGES.CA', value: 'ca' }
	];

	constructor(
		private readonly api: ApiService,
		private readonly router: Router,
		private readonly translateService: TranslateService,
		private readonly toastService: ToastService,
		public readonly newCardRequestService: NewCardRequestService,
		private readonly sanitizer: DomSanitizer,
		private readonly configurationService: ConfigurationService,
        private readonly utilsService: UtilsService
	) { }

	ngOnInit(): void {
	    window.addEventListener("message", this.receiveMessage, false);
	    this.waitingMessages = this.waitingMessagesTranslationKeys.map(key => this.translateService.instant(key));
	    // this.documentationPackValues = this.configurationService.getConfiguration().documentationPacks;
	    this.documentationPackValues = [{ packName: 'SABTAR02', packDescription: 'SABTAR02' }];
	    this.availableQueries = this.configurationService.getConfiguration().availableQueries;

	    this.newCardRequestService.setForm(this.selectedRequestorType, this.selectedRequestType);
	    this.setDocumentationPackDropdownValue();
	}

	receiveMessage = (event: MessageEvent) => {
	    const messageFromBelender = event.data as BelenderWebShocketMessage;

	    if (messageFromBelender && messageFromBelender.type == "websocket") {
	        if (BelenderStatusCodes.includes(messageFromBelender.data.status_code)) {
	            // Assert form está deshabilitado
	            if (!this.formIsDisabled) this.formIsDisabled = true;

	            this.belenderStatus.actualStatus = messageFromBelender.data.status_code;
	            this.belenderStatus.actualStatusMessage = messageFromBelender.data.status_message;

	            if (this.belenderStatus.actualStatus === "consent_process_completed") {
	                this.toastService.showSuccess('TOAST.SUCCESS', 'NEW_REQUEST.CREATED', true);
	            }
	        }
	    }
	};

	detailButtonIsVisible(): boolean {
	    return this.belenderStatus.actualStatus === "consent_process_completed";
	}

	updateTrustedUrl(newUrl: string): void {
	    this.trustedConsentUrlBoxed = this.sanitizer.bypassSecurityTrustResourceUrl(newUrl);
	}

	goToDetail(): void {
	    if (this.physicalPersonQueryResponse){
	        this.isLoadingRequest = true;
	        this.router.navigate([`/cards/request-detail/${this.physicalPersonQueryResponse.queryId}`])
	            .catch(error => { console.error(error); })
	            .finally(() => this.isLoadingRequest = false);
	    }
	}

	goToStepOne(): void {
	    this.newRequestStatus = { title: 'New request', step: 1 };
	    this.newCardRequestService.resetSidebar();
	}

	goToStepTwo(): void {
	    if (this.selectedRequestorType != '' && this.selectedRequestType != '') {
	        this.newRequestStatus = { title: 'Request data', step: 2 };
	        this.newCardRequestService.setForm(this.selectedRequestorType, this.selectedRequestType);
	    }
	}

	//the validation is moved to the confirmSubmit method
	async onSubmit(): Promise<void> {
	    this.formIsDisabled = true;

	    if (this.newCardRequestService.myForm.valid) {
	        try {
	            this.isLoading = true;
	            const _myFormCopy = { ...this.newCardRequestService.myForm.value };

	            if (this.newCardRequestService.myForm.get('birthDate')) {
	                // modifying the birthDate to the correct format
	                const _birthDate = new Date(this.newCardRequestService.myForm.value.birthDate);
	                const _formattedBirthDate = this.utilsService.formatLocalYYYYMMDD(_birthDate);
	                _myFormCopy.birthDate = _formattedBirthDate;
	            }

	            if (this.newCardRequestService.myForm.get('personIdExpirationDate')) {
	                // modifying the personIdExpirationDate to the correct format
	                const _personIdExpirationDate = new Date(this.newCardRequestService.myForm.value.personIdExpirationDate);
	                const _formattedPersonIdExpirationDate = this.utilsService.formatLocalYYYYMMDD(_personIdExpirationDate);
	                _myFormCopy.personIdExpirationDate = _formattedPersonIdExpirationDate;
	            }

	            _myFormCopy.personId = _myFormCopy.personId.toUpperCase();
	            _myFormCopy.belenderCircuit = this.newCardRequestService.selectedBelenderCircuit;
	            _myFormCopy.clientEnvironment = this.configurationService.getConfiguration().clientEnviroment;
	            _myFormCopy.sendLink = true;
	            this.physicalPersonQueryResponse = await this.api.post<PhysicalPersonQueryResponse>('cards', _myFormCopy);

	            if (!this.validateResponse(this.physicalPersonQueryResponse)) {
	                this.isLoading = false;
	                this.formIsDisabled = false;
	                return;
	            }

	            if (this.newCardRequestService.requestType !== 'Clave_Pin_SMS' && this.physicalPersonQueryResponse.consentUrlBoxed != null) {
	                this.updateTrustedUrl(this.physicalPersonQueryResponse.consentUrlBoxed);
	            }

	            this.isLoading = false;
	            if (environment.offlineMode || this.newCardRequestService.requestType === 'Clave_Pin_SMS') {
	                this.newRequestStatus = { title: 'Request summary', step: 4 };
	            } else
	                this.newRequestStatus = { title: 'Validate request', step: 3 };
	        } catch (error) {
	            this.isLoading = false;
	            this.formIsDisabled = false;
	            if (error?.status === 422 || error?.error?.status === 422) {
	                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.422_ERROR', true);
	                await this.adminMessagesComponent.setMessages();
	            } else if (error?.status === 400 && error.error === INVALID + SEPARATOR + DNI) {
	                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.INVALID_DNI', true);
	            } else if (error?.status === 400) {
	                const errors = error?.error.errors;
	                if (errors && Array.isArray(errors)) {
	                    this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.CHECK_SERVICE_VALIDATION_ERRORS', true, 6000, 'newRequestErrors', errors);
	                }else {
	                    this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.ERROR', true);
	                }
	            } else {
	                this.toastService.showError('TOAST.ERROR', 'NEW_REQUEST.ERROR', true);
	            }
	        }
	    }
	    else {
	        this.formIsDisabled = false;
	        this.checkValidationErrors();
	    }
	}

	checkValidationErrors(): void {
	    for (const key in this.newCardRequestService.myForm.controls) { // LOOP THROUGH ALL FORM CONTROLS
	        if (Object.prototype.hasOwnProperty.call(this.newCardRequestService.myForm.controls, key)) { // CHECK IF THE PROPERTY BELONGS TO THE OBJECT
	            const control = this.newCardRequestService.myForm.get(key);
	            if (control.invalid) {
	                this.checkFieldIsRequiredOrInvalid(control, key);
	            }
	        }
	    }
	    this.showValidationErrors();

	    this.invalidFields = [];
	    this.requiredFieldsMissing = false;
	}

	checkFieldIsRequiredOrInvalid(control: AbstractControl, key: string): void {
	    if (control.errors['required']) {
	        if (!this.requiredFieldsMissing) this.requiredFieldsMissing = true; // SET THE FLAG TO TRUE ONLY ONCE
	        const formField = document.querySelector(`[formControlName="${key}"]`);
	        formField.classList.add('ng-dirty', 'ng-touched'); // ADD THE CLASSES TO SHOW THE ERROR
	    } else {
	        this.invalidFields.push(`NEW_REQUEST.INVALID_FORM.${key}`);  // ADD THE INVALID FIELD TO THE ARRAY
	    }
	}

	/**
	 * if a required field is not filled, it shows a toast with a message,
	 * otherwise it shows a toast with the invalid fields
	 */
	showValidationErrors(): void {
	    if (this.requiredFieldsMissing) {
	        this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.ALL_FIELDS_REQUIRED', false, null, 'newRequestErrors');
	    } else {
	        this.toastService.showError('TOAST.INVALID', 'NEW_REQUEST.INVALID_FORM_DETAIL', false, 5000, 'newRequestErrors', this.invalidFields);
	    }
	}

	goToLastStep(): void {
	    if (this.newCardRequestService.isFinishingOTP){
	        this.newCardRequestService.isFinishingOTP = false;
	        window.location.reload();
	    }
	    this.newRequestStatus = { title: 'Request summary', step: 4 };
	}


	validateResponse(physicalPersonQueryResponse: PhysicalPersonQueryResponse): boolean {
	    if (physicalPersonQueryResponse.statusCode == 400 || physicalPersonQueryResponse.queryId == null) {
	        this.toastService.showError(`Error ${physicalPersonQueryResponse.statusCode}`, `${physicalPersonQueryResponse?.formattedErrors}`, false, 10000);  //TODO: traudcir
	        return false;
	    }

	    return true;
	}

	isFieldInvalid(fieldName: string, errorName: string): boolean {
	    const field = this.newCardRequestService.myForm.get(fieldName);

	    if (field.hasError(errorName) && (field.dirty || field.touched)) {
	        return true;
	    } else {
	        return false;
	    }
	}

	getRequestValidationInfo(): string {
	    if (this.newCardRequestService.requestType === 'Clave_Pin_SMS') {
	        return this.translateService.instant('NEW_REQUEST.PENDING_SMS_VALIDATION');
	    }
	    return this.translateService.instant('NEW_REQUEST.VALIDATED');
	}

	private setDocumentationPackDropdownValue(): void {
	    if (this.documentationPackValues?.length === 1){
	        this.newCardRequestService.myForm.get('documentationPack').setValue(this.documentationPackValues[0].packName);
	    }
	}
}
